// Renders every ingredient / drink / tool into small PNG icons using the 3D engine.
import { Scene, Camera, Node } from '../engine/renderer.js';
import { col } from '../engine/math.js';
import { INGREDIENTS, RECIPES, SLUDGE } from '../game/data.js';
import * as M from '../game/models.js';

export function buildIcons(renderer) {
  const icons = {};
  const scene = new Scene();
  const e = scene.env;
  e.sunDir = [-0.5, -0.9, -0.6];
  e.sunIntensity = 3.2;
  e.skyAmb = col('#b8b0ff').map((v) => v * 0.8);
  e.groundAmb = col('#40305a');
  e.rim = col('#b9e6ff');
  e.rimIntensity = 0.6;
  e.envIntensity = 0.8;
  scene.sky.nebIntensity = 1.4;
  const cam = new Camera();
  cam.fov = (30 * Math.PI) / 180;
  const snap = (node, key, { y = 0.12, dist = 0.95, size = 96 } = {}) => {
    cam.position = [0, y + dist * 0.62, dist * 0.8];
    cam.target = [0, y, 0];
    icons[key] = renderer.snapshot([node], cam, size, scene);
  };
  for (const id in INGREDIENTS) {
    const d = INGREDIENTS[id];
    const states = ['raw'];
    if (d.prep === 'chop') states.push('chopped');
    if (d.prep === 'grind') states.push('ground');
    for (const st of states) {
      const n = new Node(M.ingredientMesh(id, st));
      n.rotation = [0, 0.5, 0];
      // the long, flat Blender-built sprigs and gyokuro scoop get a little extra zoom
      const k = st !== 'raw' ? 1.5 : ['tea', 'matcha', 'jasmine'].includes(id) ? 1.7 : 1.25;
      n.scale = [k, k, k];
      snap(n, `ing:${id}:${st}`, { y: st === 'raw' ? 0.15 : 0.08 });
    }
  }
  const cupNode = (drinkId, fizzy) => {
    const root = new Node();
    const body = new Node();
    body.scale = [1.2, 1.2, 1.2];
    body.add(new Node(M.cupMesh(false)));
    if (drinkId) body.add(new Node(M.drinkMesh(drinkId, fizzy)));
    root.add(body);
    root.rotation = [0.42, -0.5, 0];
    root.scale = [1.45, 1.45, 1.45];
    return root;
  };
  const DRINK = { y: 0.26, dist: 1.14, size: 128 };
  // boba cups: drawn without the clear shell (icons render opaque); a pale column stands in for an empty cup
  const bobaNode = (drinkId, { pearls = true, sealed = true, straw = true } = {}) => {
    const root = new Node();
    root.add(new Node(M.bobaCupMesh()));
    if (pearls) root.add(new Node(M.bobaPearlsMesh()));
    const liq = new Node(M.bobaLiquidMesh());
    const c = drinkId ? col(RECIPES[drinkId].color) : col('#dcefff');
    liq.tint = [c[0], c[1], c[2], 1];
    root.add(liq);
    if (sealed) root.add(new Node(M.bobaSealMesh()));
    if (straw) root.add(new Node(M.bobaStrawMesh()));
    root.rotation = [0.08, -0.5, 0];
    root.scale = [1.3, 1.3, 1.3];
    return root;
  };
  const BOBA = { y: 0.3, dist: 1.25, size: 128 };
  for (const id in RECIPES) {
    if (RECIPES[id].boba) {
      snap(bobaNode(id), 'drink:' + id, BOBA);
      continue;
    }
    snap(cupNode(id, !!RECIPES[id].fizz), 'drink:' + id, DRINK);
    if (RECIPES[id].fizz) snap(cupNode(id, false), 'drink:' + id + ':flat', DRINK);
  }
  snap(cupNode('sludge', false), 'drink:sludge', DRINK);
  snap(cupNode(null), 'cup', DRINK);
  snap(bobaNode(null, { sealed: false, straw: false }), 'bobacup', BOBA);
  {
    const n = new Node(M.looseStrawMesh());
    n.rotation = [0, 0.4, 0.9];
    n.scale = [1.05, 1.05, 1.05];
    snap(n, 'straw', { y: 0.05 });
  }
  {
    const th = { accent: '#ff8a6a', accent2: '#b08aff' };
    const n = new Node(M.stationMesh('sealer', th));
    const head = new Node(M.stationMesh('sealerHead', th));
    head.position = [0, 0.62, 0.02];
    n.add(head);
    const cup = bobaNode('cosmic_milk_tea', { straw: false });
    cup.rotation = [0, 0, 0];
    cup.scale = [1, 1, 1];
    cup.position = [0, 0.07, 0.02];
    n.add(cup);
    n.rotation = [0, -0.5, 0];
    n.scale = [0.85, 0.85, 0.85];
    snap(n, 'sealer', { y: 0.4, dist: 1.9 });
  }
  {
    // spout (+x) turned to the right and a touch towards us; framed wide enough for the lid knob and the spout tip
    const n = new Node(M.kettleMesh());
    n.scale = [1.3, 1.3, 1.3];
    n.rotation = [0, -0.3, 0];
    n.position = [-0.12, 0, 0];
    snap(n, 'kettle', { y: 0.29, dist: 1.6 });
  }
  {
    const n = new Node();
    n.add(new Node(M.jugMesh()));
    const liq = new Node(M.jugLiquidMesh());
    liq.scale = [1, 0.25, 1];
    liq.position = [0, 0.06, 0];
    liq.tint = [1, 0.7, 0.4, 1];
    n.add(liq);
    n.scale = [1.1, 1.1, 1.1];
    snap(n, 'blender', { y: 0.26, dist: 1.15 });
  }
  {
    const n = new Node(M.extinguisherMesh());
    n.scale = [1.3, 1.3, 1.3];
    snap(n, 'ext', { y: 0.28 });
  }
  icons.fizz = fizzIcon();
  return icons;
}

function fizzIcon() {
  const c = document.createElement('canvas');
  c.width = c.height = 96;
  const g = c.getContext('2d');
  const bubbles = [[48, 62, 20], [28, 34, 12], [66, 28, 14], [70, 70, 9], [40, 16, 7], [24, 66, 7]];
  for (const [x, y, r] of bubbles) {
    const grd = g.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r);
    grd.addColorStop(0, 'rgba(255,255,255,0.95)');
    grd.addColorStop(0.5, 'rgba(140,255,230,0.55)');
    grd.addColorStop(1, 'rgba(60,200,255,0.25)');
    g.fillStyle = grd;
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = 'rgba(200,255,250,0.95)';
    g.lineWidth = 2.5;
    g.stroke();
  }
  return c.toDataURL();
}
